import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridRowModesModel,
  type GridFilterInputValueProps,
  type GridFilterOperator,
  useGridApiRef,
} from '@mui/x-data-grid'
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  type SelectProps,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import SearchField from 'components/search-field'
import colors from 'common/colors'
import { t } from 'i18next'
import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  fetchPartnerOrganizations,
  changeTenantSubscription,
  createNextSubscriptionPeriod,
} from 'api/partner'
import type { AppState } from 'state/index'
import _ from 'es-toolkit/compat'
import { formatCurrency } from 'common/currencyUtils'
import { trackEvent } from 'api/usage-tracking'
import { TEST_IDS } from 'test-ids'

type TenantData = {
  id: string
  name: string
  email: string
  msrp: number
  discountedMsrp: number
  currencyCode: string
  accountEnabled: boolean
  harvestedAreaSqm?: number
  endDate: string
  currentSubscription: string
  previousYearSubscription: string
  currentYearSubscriptionPeriodId: number
  currentSubscriptionStartDate: string
  nextSubscriptionPeriodStatus?: string
  nextSubscriptionPeriodId?: number
  nextSubscriptionStartDate?: string
  nextSubscriptionEndDate?: string
  subscriptionId?: number
  hasExpiredMoreThan2Periods?: boolean
}

export default function OrganizationRenewal(props) {
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [hideExpired, setHideExpired] = useState(false)

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')

  const [organizationList, setOrganizationList] = useState<TenantData[]>([])

  const [columns, setColumns] = useState<GridColDef[]>([])
  const apiRef = useGridApiRef()

  function expiryFilterInput(props: GridFilterInputValueProps) {
    const { item, applyValue } = props

    const handleFilterChange: SelectProps['onChange'] = (event, newValue) => {
      applyValue({ ...item, value: event.target.value })
    }
    return (
      <Box>
        <Select value={item.value || 365} onChange={handleFilterChange}>
          <MenuItem value={365}>{t('all')}</MenuItem>
          <MenuItem value={30}>{t('within_30_days')}</MenuItem>
          <MenuItem value={60}>{t('within_60_days')}</MenuItem>
          <MenuItem value={90}>{t('within_90_days')}</MenuItem>
        </Select>
      </Box>
    )
  }

  const expiryFilterOperator: GridFilterOperator<any, number>[] = [
    {
      label: t('period'),
      value: 'within',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) {
          return null
        }
        const now = new Date()
        const filterDate = new Date(now)
        filterDate.setDate(filterDate.getDate() + parseInt(filterItem.value))
        return (value): boolean => {
          if (!value) {
            return false
          }
          if (filterItem.value === 365) {
            return true
          }
          const cellDate = new Date(value)
          return cellDate <= filterDate && cellDate >= now
        }
      },
      InputComponent: expiryFilterInput,
      getValueAsString: (value: number) => {
        if (!value) {
          return ''
        }
        return `${value} days`
      },
    },
  ]
  function subscriptionStatusInput(props: GridFilterInputValueProps) {
    const { item, applyValue } = props

    const handleFilterChange: SelectProps['onChange'] = (event, newValue) => {
      applyValue({ ...item, value: event.target.value })
    }
    return (
      <Box>
        <Select value={item.value || 'all'} onChange={handleFilterChange}>
          <MenuItem value={'all'}>{t('all')}</MenuItem>
          <MenuItem value={'paid'}>{t('paid')}</MenuItem>
          <MenuItem value={'unpaid'}>{t('unpaid')}</MenuItem>
        </Select>
      </Box>
    )
  }

  const subscriptionStatusFilterOperator: GridFilterOperator<any, string>[] = [
    {
      label: t('status'),
      value: 'within',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) {
          return null
        }
        const now = new Date()
        const filterDate = new Date(now)
        filterDate.setDate(filterDate.getDate() + parseInt(filterItem.value))
        return (value: string): boolean => {
          if (!value) {
            return false
          }
          if (filterItem.value === 'all') {
            return true
          }
          if (filterItem.value === 'paid') {
            return (
              value === 'paid' || value === 'comped' || value.includes('free')
            )
          }
          if (filterItem.value === 'unpaid') {
            return (
              value === 'not_paid' ||
              value.includes('payment_due') ||
              value === 'new'
            )
          }
          return false
        }
      },
      InputComponent: subscriptionStatusInput,
      getValueAsString: (value: string) => {
        if (!value) {
          return ''
        }
        return t(value)
      },
    },
  ]
  function fetchData(ignore) {
    fetchPartnerOrganizations()
      .then((res) => {
        if (!ignore) {
          const { results } = res.data
          const _organizationList = (results || []).map((organization) => ({
            id: organization.tenantId,
            name: organization.organizationName,
            email: organization.email,
            msrp: organization?.msrp || 0,
            discountedMsrp: organization?.discountedMsrp || 0,
            currencyCode: organization?.currencyCode || '',
            accountEnabled: organization?.accountEnabled,
            harvestedAreaSqm: organization?.harvestedAreaSqm || 0,
            endDate: organization?.expiryDate || '',
            currentSubscription: organization?.currentYear || '',
            previousYearSubscription: organization?.previousYear || '',
            currentYearSubscriptionPeriodId:
              organization?.currentYearSubscriptionPeriodId,
            currentSubscriptionStartDate:
              organization?.currentSubscriptionStartDate || '',
            nextSubscriptionPeriodStatus:
              organization?.nextSubscriptionPeriodStatus || undefined,
            nextSubscriptionPeriodId:
              organization?.nextSubscriptionPeriodId || undefined,
            nextSubscriptionStartDate:
              organization?.nextSubscriptionStartDate || undefined,
            nextSubscriptionEndDate:
              organization?.nextSubscriptionEndDate || undefined,
            subscriptionId: organization?.subscriptionId || undefined,
            hasExpiredMoreThan2Periods:
              organization?.hasExpiredMoreThan2Periods || false,
          }))
          setOrganizationList(_organizationList)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    let ignore = false
    setLoading(true)
    fetchData(ignore)
    return () => {
      ignore = true
    }
  }, [])

  const onSearch = (e: string) => {
    setSearchValue(e)
  }

  const filterOrganizations = (searchValue, organization) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()

      return organization.name?.toLowerCase().indexOf(lowerSearchValue) > -1
    }

    return true
  }

  const changeStatus = useCallback(
    async (
      row: TenantData,
      subscriptionPeriodId: number,
      status: string,
      subscriptionPeriod: string = 'currentSubscription',
    ) => {
      changeTenantSubscription(
        row.id,
        status, // status
        subscriptionPeriodId, // subscriptionPeriodId
      ).then((res) => {
        if (res.status === 200) {
          const updatedRow = {
            ...row,
            [subscriptionPeriod]: status,
          }
          const rowIds = apiRef.current?.getAllRowIds() || []
          apiRef.current?.updateRows([updatedRow])
          trackEvent('subscription_changed', { newStatus: status })
        }
      })
    },
    [organizationList, apiRef],
  )

  const changeNextPeriodStatus = useCallback(
    async (row: TenantData, status: string) => {
      if (!row.nextSubscriptionPeriodId) return
      changeTenantSubscription(
        row.id,
        status,
        row.nextSubscriptionPeriodId,
      ).then((res) => {
        if (res.status === 200) {
          const updatedRow = {
            ...row,
            nextSubscriptionPeriodStatus: status,
          }
          apiRef.current?.updateRows([updatedRow])
          trackEvent('subscription_changed', { newStatus: status })
        }
      })
    },
    [apiRef],
  )

  const handleCreateNextPeriod = useCallback(
    async (row: TenantData) => {
      createNextSubscriptionPeriod(row.id).then((res) => {
        if (res.status === 201) {
          const createdPeriod = res.data
          const updatedRow = {
            ...row,
            nextSubscriptionPeriodStatus: createdPeriod.subscriptionStatus,
            nextSubscriptionPeriodId: createdPeriod.id,
            nextSubscriptionStartDate: createdPeriod.startDate,
            nextSubscriptionEndDate: createdPeriod.endDate,
          }
          apiRef.current?.updateRows([updatedRow])
          trackEvent('next_subscription_period_created', {
            periodId: createdPeriod.id,
          })
        }
      })
    },
    [apiRef],
  )

  const [columnVisibility, setColumnVisibility] =
    useState<GridColumnVisibilityModel>({
      id: false,
      name: true,
      email: true,
      msrp: true,
      discountedMsrp: true,
      accountEnabled: false,
      harvestedAreaSqm: false,
      currentSubscriptionRange: false,
      currentSubscription: true,
      currentSubscriptionActions: true,
      nextSubscriptionPeriod: true,
      nextSubscriptionPeriodActions: true,
      currentYearSubscriptionPeriodId: false,
      currentSubscriptionStartDate: true,
      nextSubscriptionPeriodStatus: false,
      nextSubscriptionPeriodId: false,
      nextSubscriptionStartDate: false,
      nextSubscriptionEndDate: false,
      subscriptionId: false,
    })

  const isExpiredLongerThan2Periods = (row: TenantData): boolean => {
    return row.hasExpiredMoreThan2Periods || false
  }

  useEffect(() => {
    const _columns: GridColDef[] = [
      {
        field: 'name',
        headerName: t('organization_name'),
        minWidth: 150,
        maxWidth: 300,
        flex: 1,
        filterable: false,
      },
      {
        field: 'email',
        headerName: t('email'),
        minWidth: 200,
        maxWidth: 400,
        align: 'left',
        flex: 1,
        filterable: false,
      },
      {
        field: 'msrp',
        headerName: t('msrp'),
        minWidth: 140,
        maxWidth: 250,
        align: 'left',
        flex: 1,
        filterable: false,
        renderCell: ({ row }) =>
          row.currencyCode ? formatCurrency(row.msrp, row.currencyCode) : '',
      },
      {
        field: 'discountedMsrp',
        headerName: t('partner_price'),
        minWidth: 140,
        maxWidth: 250,
        align: 'left',
        flex: 1,
        filterable: false,
        renderCell: ({ row }) =>
          row.currencyCode
            ? formatCurrency(row.discountedMsrp, row.currencyCode)
            : '',
      },
      {
        field: 'currentSubscription',
        headerName: t('status'),
        minWidth: 140,
        maxWidth: 200,
        align: 'left',
        flex: 0.8,
        filterOperators: subscriptionStatusFilterOperator,
        renderCell: (params) => {
          const isNotPaidStatus =
            params.row.currentSubscription === 'new' ||
            params.row.currentSubscription === 'not_paid' ||
            params.row.currentSubscription === 'payment_due' ||
            params.row.currentSubscription?.includes('payment_due_final')

          // Always show status text - map not paid statuses to 'not_paid'
          const statusKey = isNotPaidStatus
            ? 'not_paid'
            : params.row.currentSubscription
          return <span>{t(statusKey)}</span>
        },
      },
      {
        field: 'currentSubscriptionActions',
        headerName: '',
        minWidth: 140,
        maxWidth: 200,
        align: 'left',
        flex: 0.8,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isPaid =
            params.row.currentSubscription === 'paid' ||
            params.row.currentSubscription === 'comped' ||
            params.row.currentSubscription?.includes('free')

          // Show ONE button based on status
          if (isPaid) {
            return (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  changeStatus(
                    params.row,
                    params.row.currentYearSubscriptionPeriodId,
                    'not_paid',
                  )
                }}
                data-testid={`${TEST_IDS.organizationRenewalsSetNotPaidBtn}-${params.row.id}-${params.row.currentYearSubscriptionPeriodId}`}
              >
                <Typography sx={{ fontSize: '0.8rem' }}>
                  {t('set_to_not_paid')}
                </Typography>
              </Button>
            )
          } else {
            return (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  changeStatus(
                    params.row,
                    params.row.currentYearSubscriptionPeriodId,
                    'paid',
                  )
                }}
                data-testid={`${TEST_IDS.organizationRenewalsSetPaidBtn}-${params.row.id}-${params.row.currentYearSubscriptionPeriodId}`}
              >
                <Typography sx={{ fontSize: '0.8rem' }}>
                  {t('set_to_paid')}
                </Typography>
              </Button>
            )
          }
        },
      },
      {
        field: 'currentSubscriptionStartDate',
        headerName: t('start_date'),
        minWidth: 140,
        maxWidth: 200,
        align: 'left',
        flex: 1,
        filterable: false,
        renderCell: (params) => {
          return params.row.currentSubscriptionStartDate
            ? new Date(
                params.row.currentSubscriptionStartDate,
              ).toLocaleDateString()
            : ''
        },
      },
      {
        field: 'endDate',
        headerName: t('end_date'),
        minWidth: 140,
        maxWidth: 200,
        align: 'left',
        flex: 1,
        filterOperators: expiryFilterOperator,
        renderCell: (params) => {
          return params.row.endDate
            ? new Date(params.row.endDate).toLocaleDateString()
            : ''
        },
      },
      {
        field: 'nextSubscriptionPeriod',
        headerName: t('status'),
        minWidth: 140,
        maxWidth: 200,
        align: 'left',
        flex: 0.8,
        filterable: false,
        renderCell: (params) => {
          if (!params.row.nextSubscriptionPeriodId) return ''

          const isNotPaidStatus =
            params.row.nextSubscriptionPeriodStatus === 'new' ||
            params.row.nextSubscriptionPeriodStatus === 'not_paid' ||
            params.row.nextSubscriptionPeriodStatus === 'payment_due' ||
            params.row.nextSubscriptionPeriodStatus?.includes(
              'payment_due_final',
            )

          // Map not paid statuses to 'not_paid' for display
          const statusKey = isNotPaidStatus
            ? 'not_paid'
            : params.row.nextSubscriptionPeriodStatus
          return <span>{t(statusKey)}</span>
        },
      },
      {
        field: 'nextSubscriptionPeriodActions',
        headerName: '',
        minWidth: 140,
        maxWidth: 200,
        align: 'left',
        flex: 0.8,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const hasNextPeriod = params.row.nextSubscriptionPeriodId

          if (!hasNextPeriod) {
            // Show only "Set to Paid" button to create next period
            return (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleCreateNextPeriod(params.row)
                }}
                data-testid={`${TEST_IDS.organizationRenewalsSetPaidBtn}-${params.row.id}-next-create`}
              >
                <Typography sx={{ fontSize: '0.8rem' }}>
                  {t('set_to_paid')}
                </Typography>
              </Button>
            )
          }

          // Next period exists: show toggle button
          const isPaid =
            params.row.nextSubscriptionPeriodStatus === 'paid' ||
            params.row.nextSubscriptionPeriodStatus === 'comped' ||
            params.row.nextSubscriptionPeriodStatus?.includes('free')

          return isPaid ? (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                changeNextPeriodStatus(params.row, 'not_paid')
              }}
              data-testid={`${TEST_IDS.organizationRenewalsSetNotPaidBtn}-${params.row.id}-${params.row.nextSubscriptionPeriodId}`}
            >
              <Typography sx={{ fontSize: '0.8rem' }}>
                {t('set_to_not_paid')}
              </Typography>
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                changeNextPeriodStatus(params.row, 'paid')
              }}
              data-testid={`${TEST_IDS.organizationRenewalsSetPaidBtn}-${params.row.id}-${params.row.nextSubscriptionPeriodId}`}
            >
              <Typography sx={{ fontSize: '0.8rem' }}>
                {t('set_to_paid')}
              </Typography>
            </Button>
          )
        },
      },
    ]
    setColumns(_columns)
  }, [permissions, changeNextPeriodStatus, handleCreateNextPeriod])

  function CustomNoRowsOverlayOrganizations() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_organizations_found')}
      </div>
    )
  }

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <h1>{t('organization_renewals')}</h1>
      <SearchField
        className="partner-search"
        sx={{ width: '100%' }}
        name={searchValue}
        placeholder={t('search_by_name')}
        onSearch={onSearch}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={hideExpired}
            onChange={(e) => setHideExpired(e.target.checked)}
            data-testid={TEST_IDS.organizationRenewalsHideExpiredCheckbox}
          />
        }
        label={t('hide_expired_subscriptions')}
        sx={{ mt: 1 }}
      />
      <div
        style={{
          flexGrow: 1,
          width: '100%',
          marginTop: '2rem',
        }}
      >
        <Box
          sx={{
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            overflowX: 'auto',
            minHeight: `calc(52px * ${paginationModel?.pageSize || 10} + 56px)`,

            // 52px is approx row height, 56px for header/pagination controls
            '& .data-grid--header': {
              background: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
        >
          <DataGrid
            apiRef={apiRef}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={(model) =>
              setColumnVisibility(model)
            }
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            columnGroupingModel={[
              {
                groupId: t('current_subscription'),
                children: [
                  { field: 'currentSubscription' },
                  { field: 'currentSubscriptionActions' },
                  { field: 'currentSubscriptionStartDate' },
                  { field: 'endDate' },
                ],
              },
              {
                groupId: t('next_subscription'),
                children: [
                  { field: 'nextSubscriptionPeriod' },
                  { field: 'nextSubscriptionPeriodActions' },
                ],
              },
            ]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
              sorting: {
                sortModel: [{ field: 'endDate', sort: 'desc' }],
              },
            }}
            pagination
            paginationMode={'client'}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rows={
              (Array.isArray(organizationList) &&
                organizationList.filter((c) => {
                  const matchesSearch = filterOrganizations(searchValue, c)
                  const matchesExpiredFilter = hideExpired
                    ? !isExpiredLongerThan2Periods(c)
                    : true
                  return matchesSearch && matchesExpiredFilter
                })) ||
              []
            }
            loading={loading}
            slots={{
              noRowsOverlay: CustomNoRowsOverlayOrganizations,
            }}
            columns={columns}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: colors.greyBackground,
                textTransform: 'uppercase',
              },
            }}
          />
        </Box>
      </div>
    </Box>
  )
}
